import React, { useEffect, useRef } from 'react';
import { Box, Button } from '@mui/material';
import { useTranslation } from 'react-i18next';
import NotificationsNoneIcon from '@mui/icons-material/NotificationsNone';
import { EmptyPlaceholder } from '@components/EmptyPlaceholder';
import { EndOfFeed } from '@components/feed/EndOfFeed';
import { Feed } from '@components/feed/Feed';
import { Loader } from '@components/feed/Loader';
import { TimeProvider } from '@context/TimeContext';
import {
  useGetMyNotificationsFeed,
  useGetMyUnreadNotificationCount,
  useMarkAllMyNotificationsRead,
} from '@reactQuery/notifications';
import { useOpenNotification } from '@hooks/useOpenNotification';
import { NotificationCard } from './components/NotificationCard';

const NOTIFICATIONS_PER_PAGE = 10;

export const NotificationsPage: React.FC = (): JSX.Element => {
  const { t } = useTranslation();
  const loaderRef = useRef<HTMLDivElement | null>(null);
  const openNotification = useOpenNotification();
  const { data: unreadCount = 0 } = useGetMyUnreadNotificationCount();
  const { mutate: markAllRead, isPending: isMarkingAllRead } =
    useMarkAllMyNotificationsRead();
  const { data, isLoading, fetchNextPage, hasNextPage, isFetchingNextPage } =
    useGetMyNotificationsFeed(NOTIFICATIONS_PER_PAGE);

  const notifications = data?.pages.flatMap((page) => page.data) ?? [];

  useEffect(() => {
    if (!loaderRef.current || !hasNextPage) {
      return;
    }

    const observer = new IntersectionObserver((entries) => {
      if (entries[0].isIntersecting) {
        fetchNextPage();
      }
    });

    observer.observe(loaderRef.current);

    return () => observer.disconnect();
  }, [fetchNextPage, hasNextPage]);

  return (
    <TimeProvider>
      <Feed>
        {unreadCount > 0 && (
          <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
            <Button
              size="small"
              onClick={() => markAllRead()}
              disabled={isMarkingAllRead}
            >
              {t('notifications:markAllRead')}
            </Button>
          </Box>
        )}

        {notifications.map((notification) => (
          <NotificationCard
            key={notification.id}
            notification={notification}
            onClick={openNotification}
          />
        ))}

        {(hasNextPage || isLoading) && (
          <div ref={loaderRef} style={{ height: '40px' }}>
            {(isLoading || isFetchingNextPage) && <Loader />}
          </div>
        )}

        {!isLoading && notifications.length === 0 && (
          <EmptyPlaceholder
            title={t('notifications:empty.title')}
            subTitle={t('notifications:empty.subTitle')}
            icon={NotificationsNoneIcon}
          />
        )}

        {!hasNextPage && notifications.length > 0 && <EndOfFeed />}
      </Feed>
    </TimeProvider>
  );
};
